import globals from '@/utils/globals';

import { SignInEvent } from './events/signin';
import { OptInEvent } from './enums/optin-event.enum';
import { PortalType, IncentiveLocation, IncentiveType } from 'interfaces';
import { AuthenticationProviderType } from '@/domain/enums/authentication-provider-type';
import { PopUpType } from '@/domain/enums/pop-up-type';
import { IncentiveEvent } from './events/incentive';
import { RegisterEvent } from './events/register';
import { TrackingEvent } from './events/tracking-event';



/* Parameterized helpers reusing the shared, already-stateless Tracking instance
	(globals.authenticationServiceTracking), mirroring what src/store/global/actionHelpers.ts does
	for v1 but taking everything as explicit arguments instead of reading globalStore.getters.*. The
	v2 domain has no referrer, so null is passed for the legacy event classes' referrer parameter.
	See docs/incentive-architecture.md section 4.6. */

export const trackIncentiveRegistration = (
	portalType: PortalType | null,
	authProvider: AuthenticationProviderType,
	location?: IncentiveLocation,
	incentiveType?: IncentiveType,
	additionalTags: string[] = [],
	uniqueIdentifier?: string,
):void => {
	const tags = buildLegacyTrackingTags(incentiveType, location, uniqueIdentifier, additionalTags);
	const referrer = buildLegacyRegistrationReferrer(incentiveType, location, uniqueIdentifier);
	const registerEvent = new RegisterEvent(
		portalType,
		referrer,
		authProvider,
		tags,
	);

	trackAuthentication(registerEvent);
};

export const trackIncentiveLogin = (
	portalType: PortalType | null,
	authProvider: AuthenticationProviderType,
	location?: IncentiveLocation,
	incentiveType?: IncentiveType,
	additionalTags: string[] = [],
	uniqueIdentifier?: string,
): void => {
	const tags = buildLegacyTrackingTags(incentiveType, location, uniqueIdentifier, additionalTags);
	const signinEvent = new SignInEvent(
		portalType,
		incentiveType ?? null,
		authProvider,
		tags,
	);

	trackAuthentication(signinEvent);
};

const buildLegacyTrackingTags = (
	incentiveType?: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string,
	additionalTags: string[] = [],
): string[] => {
	const tagsSet = new Set([...buildLegacyRegistrationTags(incentiveType, location, uniqueIdentifier), ...additionalTags]);
	return Array.from(tagsSet);
};

const trackAuthentication = (
	trackingEvent: TrackingEvent,
): void => {
	globals.authenticationServiceTracking.track(
		trackingEvent,
		true,
		true,
	);
};

export const buildLegacyRegistrationReferrer = (
	incentiveType?: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string
): string => {
	return [incentiveType, location, uniqueIdentifier].filter(Boolean).join('|');
};

export const buildLegacyRegistrationTags = (
	incentiveType?: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string
): string[] => {

	return [
		incentiveType,
		location,
		uniqueIdentifier,
	].filter((tag): tag is string => Boolean(tag));
};

export const getPageType = (): string | undefined => {
	const { pathname } = new URL(window.location.href);

	const segments = pathname.split('/').filter(Boolean);
	if (segments.length === 0) return 'Home';

	const hasSearch = segments.includes('search');
	const hasUniversities = segments.some(segment =>
		segment.includes('universities'),
	);
	const hasScholarships = segments.some(segment =>
		segment.includes('scholarships'),
	);

	if (hasSearch) {
		if (hasUniversities) return 'UniversitiesSearch';

		if (hasScholarships) return 'ScholarshipsSearch';

		return 'ProgrammeSearch';
	}

	const [part1, part2] = segments;


	const name = part1.charAt(0).toUpperCase() + part1.slice(1);
	const isDetailsPage = part2 !== undefined && /^\d+$/.test(part2);

	return isDetailsPage
		? `${name}Details`
		: `${name}Overview`;
};

export const trackIncentiveOptIn = (
	event: OptInEvent,
	incentiveType?: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string,
	variation: PopUpType = PopUpType.OPT_INT_POP_UP,
): void => {
	globals.authenticationServiceTracking.trackOptInEvent(
		event,
		variation,
	);
	trackIncentiveAction(event, incentiveType, location, uniqueIdentifier);
};

export const trackIncentiveImpression = (
	incentiveType: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string,
): void => {
	const tags = buildLegacyRegistrationTags(incentiveType, location, uniqueIdentifier);
	globals.authenticationServiceTracking.track(
		new IncentiveEvent(undefined,tags),
		true,
		true,
	);
};

export const trackIncentiveAction = (
	action: string,
	incentiveType?: IncentiveType,
	location?: IncentiveLocation,
	uniqueIdentifier?: string,
): void => {
	const tags = buildLegacyRegistrationTags(incentiveType, location, uniqueIdentifier);
	globals.authenticationServiceTracking.track(
		new IncentiveEvent(action, tags),
		true,
		true,
	);
};
